import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  Download, 
  ToggleLeft, 
  ToggleRight,
  MapPin,
  Phone,
  Mail,
  Users,
  Building,
  AlertCircle,
  X
} from 'lucide-react';
import { 
  getAdminVenues, 
  createAdminVenue, 
  updateAdminVenue, 
  deleteAdminVenue, 
  updateVenueStatus,
  exportVenueData
} from '../../lib/admin';
import VenueFormModal from './VenueFormModal';
import type { AdminVenue, VenueFormData } from '../../types/admin';

export default function VenueManagement() {
  const [venues, setVenues] = useState<AdminVenue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingVenue, setEditingVenue] = useState<AdminVenue | null>(null);
  const [selectedVenue, setSelectedVenue] = useState<AdminVenue | null>(null);

  useEffect(() => {
    loadVenues();
  }, []);

  const loadVenues = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getAdminVenues();
      setVenues(data);
    } catch (err) {
      console.error('Error loading venues:', err);
      setError('Failed to load venues');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVenue = async (venueData: VenueFormData) => {
    try {
      await createAdminVenue(venueData);
      setShowFormModal(false);
      await loadVenues();
    } catch (err) {
      console.error('Error creating venue:', err);
      throw err;
    }
  };

  const handleUpdateVenue = async (venueData: VenueFormData) => {
    if (!editingVenue) return;
    
    try {
      await updateAdminVenue(editingVenue.id, venueData);
      setEditingVenue(null);
      setShowFormModal(false);
      await loadVenues();
    } catch (err) {
      console.error('Error updating venue:', err);
      throw err;
    }
  };

  const handleDeleteVenue = async (venueId: string, venueName: string) => {
    if (!confirm(`Are you sure you want to delete ${venueName}? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteAdminVenue(venueId);
      await loadVenues();
    } catch (err) {
      console.error('Error deleting venue:', err);
      setError('Failed to delete venue');
    }
  };

  const handleStatusChange = async (venueId: string, newStatus: 'active' | 'inactive' | 'maintenance') => {
    try {
      await updateVenueStatus(venueId, newStatus);
      await loadVenues();
    } catch (err) {
      console.error('Error updating venue status:', err);
      setError('Failed to update venue status');
    }
  };

  const filteredVenues = venues.filter(venue => {
    const matchesSearch = 
      venue.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      venue.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      venue.contact_email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || venue.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-900/50 text-green-200';
      case 'inactive':
        return 'bg-red-900/50 text-red-200';
      case 'maintenance':
        return 'bg-yellow-900/50 text-yellow-200';
      default:
        return 'bg-gray-900/50 text-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Venue Management</h2>
          <p className="text-gray-300">Manage wedding venues and event spaces</p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => exportVenueData()}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
          <button
            onClick={() => setShowFormModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600"
          >
            <Plus className="w-4 h-4" />
            <span>Add Venue</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/50 border border-red-800 text-red-200 p-3 rounded-md flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search venues..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="maintenance">Maintenance</option>
          </select>

          <div className="text-sm text-gray-300 flex items-center">
            Showing {filteredVenues.length} of {venues.length} venues
          </div>
        </div>
      </div>

      {/* Venues Table */}
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Venue Details
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Contact Information
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Capacity & Amenities
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredVenues.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">No venues found</p>
                    <p className="text-gray-500 text-sm">Add your first venue to get started</p>
                  </td>
                </tr>
              ) : (
                filteredVenues.map((venue) => (
                  <tr key={venue.id} className="hover:bg-gray-700/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        {venue.photos.length > 0 && (
                          <img
                            src={venue.photos[0]}
                            alt={venue.name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        )}
                        <div>
                          <p className="font-medium text-white">{venue.name}</p>
                          <div className="flex items-center space-x-1 text-sm text-gray-400">
                            <MapPin className="w-3 h-3" />
                            <span className="truncate max-w-48">{venue.address}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2 text-sm text-gray-300">
                          <Mail className="w-4 h-4" />
                          <span>{venue.contact_email}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-300">
                          <Phone className="w-4 h-4" />
                          <span>{venue.contact_phone}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2 text-sm text-gray-300">
                          <Users className="w-4 h-4" />
                          <span>Up to {venue.capacity_details?.max_capacity || 0} guests</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {venue.amenities.slice(0, 2).map((amenity, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-blue-900/50 text-blue-200 text-xs rounded-full"
                            >
                              {amenity}
                            </span>
                          ))}
                          {venue.amenities.length > 2 && (
                            <span className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded-full">
                              +{venue.amenities.length - 2}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={venue.status}
                        onChange={(e) => handleStatusChange(venue.id, e.target.value as 'active' | 'inactive' | 'maintenance')}
                        className={`px-3 py-1 rounded-full text-sm font-medium border-0 ${getStatusColor(venue.status)}`}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="maintenance">Maintenance</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setSelectedVenue(venue)}
                          className="p-2 text-blue-400 hover:text-blue-300 hover:bg-gray-700/50 rounded-md"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingVenue(venue);
                            setShowFormModal(true);
                          }}
                          className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-gray-700/50 rounded-md"
                          title="Edit Venue"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteVenue(venue.id, venue.name)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-gray-700/50 rounded-md"
                          title="Delete Venue"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Venue Form Modal */}
      {showFormModal && (
        <VenueFormModal
          venue={editingVenue}
          onClose={() => {
            setShowFormModal(false);
            setEditingVenue(null);
          }}
          onSubmit={editingVenue ? handleUpdateVenue : handleCreateVenue}
        />
      )}

      {/* Venue Details Modal */}
      {selectedVenue && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800/95 backdrop-blur-sm rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Venue Details</h3>
              <button
                onClick={() => setSelectedVenue(null)}
                className="p-2 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-gray-900/50 p-4 rounded-lg">
                  <h4 className="font-medium text-white mb-3">Basic Information</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-400">Name:</span> <span className="text-white">{selectedVenue.name}</span></p>
                    <p><span className="text-gray-400">Address:</span> <span className="text-white">{selectedVenue.address}</span></p>
                    <p><span className="text-gray-400">Email:</span> <span className="text-white">{selectedVenue.contact_email}</span></p>
                    <p><span className="text-gray-400">Phone:</span> <span className="text-white">{selectedVenue.contact_phone}</span></p>
                    <p><span className="text-gray-400">Status:</span> 
                      <span className={`ml-1 px-2 py-1 rounded-full text-xs ${getStatusColor(selectedVenue.status)}`}>
                        {selectedVenue.status.charAt(0).toUpperCase() + selectedVenue.status.slice(1)}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="bg-gray-900/50 p-4 rounded-lg">
                  <h4 className="font-medium text-white mb-3">Capacity Details</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-400">Max Capacity:</span> <span className="text-white">{selectedVenue.capacity_details?.max_capacity || 0} guests</span></p>
                    <p><span className="text-gray-400">Seated:</span> <span className="text-white">{selectedVenue.capacity_details?.seated_capacity || 0} guests</span></p>
                    <p><span className="text-gray-400">Standing:</span> <span className="text-white">{selectedVenue.capacity_details?.standing_capacity || 0} guests</span></p>
                  </div>
                </div>

                <div className="bg-gray-900/50 p-4 rounded-lg">
                  <h4 className="font-medium text-white mb-3">Amenities</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedVenue.amenities.map((amenity, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-green-900/50 text-green-200 text-sm rounded-full"
                      >
                        {amenity}
                      </span>
                    ))}
                  </div>
                </div>

                {selectedVenue.parking_info && (
                  <div className="bg-gray-900/50 p-4 rounded-lg">
                    <h4 className="font-medium text-white mb-3">Parking Information</h4>
                    <p className="text-gray-300 text-sm">{selectedVenue.parking_info}</p>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {selectedVenue.photos.length > 0 && (
                  <div className="bg-gray-900/50 p-4 rounded-lg">
                    <h4 className="font-medium text-white mb-3">Photos</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedVenue.photos.slice(0, 4).map((photo, index) => (
                        <img
                          key={index}
                          src={photo}
                          alt={`${selectedVenue.name} photo ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-gray-900/50 p-4 rounded-lg">
                  <h4 className="font-medium text-white mb-3">Facilities</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedVenue.facilities.map((facility, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-purple-900/50 text-purple-200 text-sm rounded-full"
                      >
                        {facility}
                      </span>
                    ))}
                  </div>
                </div>

                {selectedVenue.catering_options.length > 0 && (
                  <div className="bg-gray-900/50 p-4 rounded-lg">
                    <h4 className="font-medium text-white mb-3">Catering Options</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedVenue.catering_options.map((option, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-orange-900/50 text-orange-200 text-sm rounded-full"
                        >
                          {option}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedVenue.accessibility_features.length > 0 && (
                  <div className="bg-gray-900/50 p-4 rounded-lg">
                    <h4 className="font-medium text-white mb-3">Accessibility Features</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedVenue.accessibility_features.map((feature, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-teal-900/50 text-teal-200 text-sm rounded-full"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}